import { KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DataKeys } from "../../../types";
import { HealthProductSuiteIcon } from "../HealthProductSuiteIcons";
import {
    getLocalized,
    HealthLang,
    isAimmaProductId,
    LocalizedString,
} from "../healthTypes";
import {
    healthClinicalAgentsTab,
    healthClinicalAgentsTabActive,
    healthClinicalAgentsTabContent,
    healthClinicalAgentsTabInactive,
    healthClinicalAgentsTabList,
    healthClinicalAgentsTabNumber,
    healthClinicalAgentsTabRow,
    healthClinicalAgentsTabSubtitle,
    healthClinicalAgentsTabSubtitleActive,
    healthClinicalAgentsTabTitle,
    healthFocusVisible,
} from "../healthVisualStyles";

interface HealthClinicalAgentTabSelectorProps {
    blocks: DataKeys[];
    activeIndex: number;
    onSelect: (index: number) => void;
    lang: HealthLang;
    panelIdPrefix: string;
    tabListAriaLabel: string;
    tabPreviousAriaLabel: string;
    tabNextAriaLabel: string;
    renderTabIcon?: (block: DataKeys, isActive: boolean) => React.ReactNode;
}

const SCROLL_EDGE_THRESHOLD = 4;

const scrollFadeBase =
    "absolute inset-y-0 z-[1] flex w-12 items-center transition-opacity duration-200 motion-reduce:transition-none lg:w-14";

const scrollArrowButton =
    `${healthFocusVisible} pointer-events-auto flex h-full w-full items-center justify-center text-grey-400 transition-colors duration-200 hover:text-teal-600 disabled:pointer-events-none disabled:opacity-0`;

export function HealthClinicalAgentTabSelector({
    blocks,
    activeIndex,
    onSelect,
    lang,
    panelIdPrefix,
    tabListAriaLabel,
    tabPreviousAriaLabel,
    tabNextAriaLabel,
    renderTabIcon,
}: HealthClinicalAgentTabSelectorProps) {
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollHints = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        setCanScrollLeft(scrollLeft > SCROLL_EDGE_THRESHOLD);
        setCanScrollRight(
            scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_THRESHOLD
        );
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        updateScrollHints();

        container.addEventListener("scroll", updateScrollHints, { passive: true });
        const resizeObserver = new ResizeObserver(updateScrollHints);
        resizeObserver.observe(container);

        return () => {
            container.removeEventListener("scroll", updateScrollHints);
            resizeObserver.disconnect();
        };
    }, [blocks.length, updateScrollHints]);

    useEffect(() => {
        const container = containerRef.current;
        const tab = tabRefs.current[activeIndex];
        if (!container || !tab) return;

        const tabStart = tab.offsetLeft;
        const tabEnd = tabStart + tab.offsetWidth;
        const viewStart = container.scrollLeft;
        const viewEnd = viewStart + container.clientWidth;
        const visible = tabStart >= viewStart && tabEnd <= viewEnd;

        if (!visible) {
            const padding = 20;
            const nextScroll =
                tabEnd > viewEnd
                    ? tabEnd - container.clientWidth + padding
                    : Math.max(0, tabStart - padding);
            container.scrollTo({ left: nextScroll, behavior: "smooth" });
        }

        updateScrollHints();
        const syncTimer = window.setTimeout(updateScrollHints, 320);

        return () => {
            window.clearTimeout(syncTimer);
        };
    }, [activeIndex, updateScrollHints]);

    const handleTabKeyDown = (
        event: KeyboardEvent<HTMLButtonElement>,
        index: number
    ) => {
        if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) {
            return;
        }
        event.preventDefault();
        const lastIndex = blocks.length - 1;
        const nextIndex =
            event.key === "ArrowRight"
                ? Math.min(index + 1, lastIndex)
                : event.key === "ArrowLeft"
                  ? Math.max(index - 1, 0)
                  : event.key === "Home"
                    ? 0
                    : lastIndex;
        if (nextIndex === index) return;
        onSelect(nextIndex);
        tabRefs.current[nextIndex]?.focus();
    };

    const handleScrollStep = (direction: -1 | 1) => {
        const nextIndex = activeIndex + direction;
        if (nextIndex < 0 || nextIndex >= blocks.length) return;
        onSelect(nextIndex);
        tabRefs.current[nextIndex]?.focus();
    };

    const canSelectPrevious = activeIndex > 0;
    const canSelectNext = activeIndex < blocks.length - 1;

    return (
        <div className={healthClinicalAgentsTabRow}>
            <div
                ref={containerRef}
                className={healthClinicalAgentsTabList}
                role="tablist"
                aria-label={tabListAriaLabel}
            >
                {blocks.map((block, index) => {
                    const isActive = index === activeIndex;
                    const stepLabel = String(index + 1).padStart(2, "0");
                    const stepTitle = getLocalized(
                        block.title as LocalizedString,
                        lang
                    );
                    const tabDescriptor = getLocalized(
                        block.tabDescriptor as LocalizedString,
                        lang
                    );
                    const productId = block.id as string;
                    const panelId = `${panelIdPrefix}-${productId}`;
                    const iconProductId = isAimmaProductId(productId)
                        ? productId
                        : undefined;

                    return (
                        <button
                            key={productId}
                            ref={(el) => {
                                tabRefs.current[index] = el;
                            }}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={panelId}
                            id={`${panelId}-tab`}
                            onClick={() => onSelect(index)}
                            onKeyDown={(event) => handleTabKeyDown(event, index)}
                            className={`${healthClinicalAgentsTab} ${healthFocusVisible} ${
                                isActive
                                    ? healthClinicalAgentsTabActive
                                    : healthClinicalAgentsTabInactive
                            }`}
                        >
                            {renderTabIcon ? (
                                renderTabIcon(block, isActive)
                            ) : iconProductId ? (
                                <HealthProductSuiteIcon
                                    size="group"
                                    productId={iconProductId}
                                    muted={!isActive}
                                    bare
                                    className="!h-7 !w-7 lg:!h-9 lg:!w-9"
                                />
                            ) : null}
                            <span className={healthClinicalAgentsTabContent}>
                                <span className={healthClinicalAgentsTabNumber}>
                                    {stepLabel}
                                </span>
                                <span className={healthClinicalAgentsTabTitle}>
                                    {stepTitle}
                                </span>
                                {tabDescriptor ? (
                                    <span
                                        className={`${healthClinicalAgentsTabSubtitle} ${
                                            isActive
                                                ? healthClinicalAgentsTabSubtitleActive
                                                : ""
                                        }`}
                                    >
                                        {tabDescriptor}
                                    </span>
                                ) : null}
                            </span>
                        </button>
                    );
                })}
            </div>
            <div
                className={`${scrollFadeBase} pointer-events-none left-0 justify-start bg-gradient-to-r from-white via-white/80 to-transparent pl-1 ${
                    canScrollLeft || canSelectPrevious ? "opacity-100" : "opacity-0"
                }`}
            >
                <button
                    type="button"
                    className={`${scrollArrowButton} justify-start pl-0.5`}
                    aria-label={tabPreviousAriaLabel}
                    disabled={!canSelectPrevious}
                    onClick={() => handleScrollStep(-1)}
                >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
                </button>
            </div>
            <div
                className={`${scrollFadeBase} pointer-events-none right-0 justify-end bg-gradient-to-l from-white via-white/80 to-transparent pr-1 ${
                    canScrollRight || canSelectNext ? "opacity-100" : "opacity-0"
                }`}
            >
                <button
                    type="button"
                    className={`${scrollArrowButton} justify-end pr-0.5`}
                    aria-label={tabNextAriaLabel}
                    disabled={!canSelectNext}
                    onClick={() => handleScrollStep(1)}
                >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
                </button>
            </div>
        </div>
    );
}
